import { type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  COBRAND_AIRLINES,
  COBRAND_PARTNERS_INFOS,
  SUBSCRIBED_BONUSES,
  COBRAND_PARTNER_NAME_ENUM,
  PAYMENT_CARD_TYPE,
  VISA_CARD_TYPE,
  FAST_TRACK
} from '@/constants/loyalty'
import { type SubscribedBonus, type CoBrandedCard } from '@/stores/user'
import { convertDateFromServerFormat, isSameOrAfterDateNow } from '@/helpers/date-helpers'
import type { ValueOffer } from '@/components/organisms/loyalty/partnerBlockListItem.type'

export const useCoBrandedCardsAndSubscribedBonuses = () => {
  const { t } = useI18n()

  const eligibleProgramName: string[] = Object.values(COBRAND_PARTNER_NAME_ENUM)

  const extractCardTypeFromString = (inputString: string) => {
    return VISA_CARD_TYPE.find((name: string) => inputString.includes(name)) ?? ''
  }

  const foundOffer = (comparedId: string, offersEligibleLink: Ref<ValueOffer[]>) =>
    (offersEligibleLink.value as ValueOffer[]).find(({ wiseId }) => wiseId === comparedId)

  const descriptionChoice = (
    type: string,
    bookingBeginDate: string,
    bookingEndDate: string,
    stayBeginDate: string,
    stayEndDate: string
  ) => {
    const descriptionType = type === FAST_TRACK ? 'fasttrack.details' : 'section.bonus.bookingDate'

    return t(descriptionType, {
      startBookingDate: convertDateFromServerFormat(bookingBeginDate),
      endBookingDate: convertDateFromServerFormat(bookingEndDate),
      startStayDate: convertDateFromServerFormat(stayBeginDate),
      endStayDate: convertDateFromServerFormat(stayEndDate)
    })
  }

  const getCoBrandedCardsFilterByAirline = (coBrandedCards: Array<CoBrandedCard>) => {
    const coBrandAirlineFiltered = coBrandedCards?.find(
      ({ partnerCode }) => COBRAND_AIRLINES[partnerCode]
    )
    if (!coBrandAirlineFiltered) return

    return {
      ...COBRAND_AIRLINES[coBrandAirlineFiltered?.partnerCode],
      subscriptionDate: coBrandAirlineFiltered?.subscriptionDate
        ? convertDateFromServerFormat(coBrandAirlineFiltered.subscriptionDate)
        : '',
      partnerCode: coBrandAirlineFiltered?.partnerCode
    }
  }

  const getCoBrandedCardsFilterByPartnerWithAdditionalInfo = (
    coBrandedCards: Array<CoBrandedCard>
  ) => {
    const coBrandedCardsFilterByPartner = coBrandedCards?.filter(
      ({ partnerCode, partnerName }) =>
        COBRAND_PARTNERS_INFOS[partnerCode] ?? COBRAND_PARTNERS_INFOS[partnerName]
    )

    return coBrandedCardsFilterByPartner.map((cobrand) => {
      // BNP and ADCB don't have valid partnerCode so we have to use their partnerName
      const result =
        COBRAND_PARTNERS_INFOS[cobrand.partnerCode] ?? COBRAND_PARTNERS_INFOS[cobrand.partnerName]

      const urlMap: Record<string, string> = {
        [COBRAND_PARTNER_NAME_ENUM.BNP]:
          PAYMENT_CARD_TYPE[extractCardTypeFromString(cobrand.programmeName)],
        [COBRAND_PARTNER_NAME_ENUM.WOORI]: t('cobrand.woori.url'),
        [COBRAND_PARTNER_NAME_ENUM.CIMB]: t('cobrand.cimbNiaga.url')
      }

      return {
        programName: eligibleProgramName.includes(cobrand.partnerName) ? cobrand.programmeName : '',
        subscriptionDate: cobrand.subscriptionDate
          ? convertDateFromServerFormat(cobrand.subscriptionDate)
          : '',
        url: urlMap[cobrand.partnerName] || '',
        ...result
      }
    })
  }

  const getSubscribedBonusesFilterWithAdditionalInfo = (
    subscribedBonuses: Array<SubscribedBonus>,
    offersEligibleLink: Ref<ValueOffer[]>
  ) => {
    const subscribedBonusesFilterByDate = subscribedBonuses.filter(
      ({ type, criteriaBonus, informationBonus: { wiseId } }) => {
        if (type === FAST_TRACK) {
          return (
            criteriaBonus?.bookingEndDate && isSameOrAfterDateNow(criteriaBonus?.bookingEndDate)
          )
        } else {
          return (
            !!foundOffer(wiseId, offersEligibleLink) &&
            criteriaBonus?.stayEndDate &&
            isSameOrAfterDateNow(criteriaBonus?.stayEndDate)
          )
        }
      }
    )

    return subscribedBonusesFilterByDate.map(
      ({
        type,
        criteriaBonus: { bookingBeginDate, bookingEndDate, stayBeginDate, stayEndDate },
        informationBonus: { wiseId }
      }) => {
        const result = SUBSCRIBED_BONUSES[type] ?? SUBSCRIBED_BONUSES['default']

        return {
          ...result,
          ...foundOffer(wiseId, offersEligibleLink),
          description: descriptionChoice(
            type,
            bookingBeginDate,
            bookingEndDate,
            stayBeginDate,
            stayEndDate
          ),
          condition:
            type === FAST_TRACK
              ? t('fasttrack.conditions', {
                  endStayDate: convertDateFromServerFormat(stayEndDate)
                })
              : ''
        }
      }
    )
  }

  return {
    getCoBrandedCardsFilterByPartnerWithAdditionalInfo,
    getSubscribedBonusesFilterWithAdditionalInfo,
    getCoBrandedCardsFilterByAirline
  }
}
